import { useEffect, type ReactNode } from 'react';

import { useCurrentClinicId } from '../auth/index.js';
import { useClinicTheme } from './queries.js';
import { PALETTES, type PaletteKey } from './palettes.js';

export interface PaletteThemeProviderProps {
  children: ReactNode;
}

/**
 * Componente que aplica a paleta de cores escolhida pela clinic do
 * owner ao `<html>` (via atributo `data-palette` + injeta CSS vars
 * inline pra `:root`).
 *
 * **Onde montar**: envolver toda árvore autenticada da clinic — ideal
 * dentro de `<RequireAuth>` antes do shell. Quando user tem
 * `current_clinic_id` no JWT, busca paleta da clinic. Quando não tem
 * (deslogado, superadmin, etc), remove qualquer override e deixa os
 * tokens globais do brand book prevalecerem.
 *
 * **Estratégia técnica**:
 *   - Atributo `data-palette="<key>"` no `<html>` — útil pra debug e
 *     pra CSS opcional `[data-palette="X"] { ... }` se quisermos
 *     adicionar regras específicas no futuro.
 *   - CSS vars (`--primary`, `--accent`, etc) injetadas via
 *     `document.documentElement.style.setProperty` baseadas no modo
 *     atual (light/dark). Sobrescrevem as defaults de
 *     `globals.css`.
 *   - Detecção light/dark: checa `class="dark"` ou `data-theme="dark"`
 *     na `<html>`. Suporta tanto sistema (prefers-color-scheme) quanto
 *     toggle manual.
 *
 * Não usa Context React — paleta é "ambiente" (CSS vars), todo
 * componente herda automaticamente. Provider só side-effect.
 *
 * Mudança de paleta (via `/identidade-visual`) → mutation invalida
 * cache → query refaz → este Provider re-renderiza → seta CSS vars
 * novas → toda UI atualiza visualmente sem reload.
 */
export function PaletteThemeProvider({ children }: PaletteThemeProviderProps) {
  const clinicId = useCurrentClinicId();
  const themeQ = useClinicTheme(clinicId);
  const palette: PaletteKey | null = clinicId ? (themeQ.data?.theme_palette ?? 'default') : null;

  useEffect(() => {
    const html = document.documentElement;

    if (!palette) {
      html.removeAttribute('data-palette');
      html.style.removeProperty('--primary');
      html.style.removeProperty('--primary-foreground');
      html.style.removeProperty('--accent');
      html.style.removeProperty('--accent-foreground');
      return undefined;
    }

    html.setAttribute('data-palette', palette);

    // Detecta modo: classe `dark` (Tailwind dark mode class strategy)
    // OU `data-theme="dark"` (storybook/teste). Default: light.
    const isDark = html.classList.contains('dark') || html.getAttribute('data-theme') === 'dark';

    const tokens = isDark ? PALETTES[palette].dark : PALETTES[palette].light;

    // Sobrescreve CSS vars semânticas. Outras (background, card, muted,
    // border) ficam herdadas do `globals.css` — neutras entre paletas.
    html.style.setProperty('--primary', tokens.primary);
    html.style.setProperty('--primary-foreground', tokens.primaryForeground);
    html.style.setProperty('--accent', tokens.accent);
    html.style.setProperty('--accent-foreground', tokens.accentForeground);

    // Cleanup: ao desmontar (logout, etc), reverte pra default. Evita
    // que paleta exótica de uma clinic "vaze" pra tela de login depois
    // do logout.
    return () => {
      html.removeAttribute('data-palette');
      html.style.removeProperty('--primary');
      html.style.removeProperty('--primary-foreground');
      html.style.removeProperty('--accent');
      html.style.removeProperty('--accent-foreground');
    };
  }, [palette]);

  // Re-aplica quando `class="dark"` muda (toggle manual de tema).
  // Ouve mutation no <html> e re-roda o effect com paleta atual.
  useEffect(() => {
    if (!palette) return undefined;

    const html = document.documentElement;
    const observer = new MutationObserver(() => {
      const isDark = html.classList.contains('dark') || html.getAttribute('data-theme') === 'dark';
      const tokens = isDark ? PALETTES[palette].dark : PALETTES[palette].light;
      html.style.setProperty('--primary', tokens.primary);
      html.style.setProperty('--primary-foreground', tokens.primaryForeground);
      html.style.setProperty('--accent', tokens.accent);
      html.style.setProperty('--accent-foreground', tokens.accentForeground);
    });
    observer.observe(html, { attributes: true, attributeFilter: ['class', 'data-theme'] });
    return () => observer.disconnect();
  }, [palette]);

  return <>{children}</>;
}
